/* eslint-disable no-nested-ternary */
/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Formik } from 'formik';
import Box from '@mui/material/Box';
import { toast } from 'react-toastify';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { LoadedUser } from '../../interfaces';
import './form.css';
import {
  initFormValues,
  getCheckoutSchema,
} from '../../utils/form/checkoutForm';
import {
  formatDollarAmount,
  isUserAdminOrManager,
} from '../../utils/global/helper';
import {
  closeCheckout as closeCheckoutSuccess,
  closeOrderCheckout,
  invalidateAppointment,
} from '../../services/appointments';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import { AppContext } from '../../context/AppContext/context';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import useUser from '../../hooks/global/useUser';
import { addCustomerAlgolia } from '../../services/user';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useConfirm from '../../hooks/useConfirm';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
  SelectedAppointment,
} from '../../types/appointments';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import useBusinessWebRedesign from '../../hooks/businesses/useBusinessWebRedesign';
import { Feature } from '../../types/subscriptions';
import useStripeOptions from '../../hooks/stripe/useStripeOptions';
import { STRIPE_PK } from '../../constants/stripe';
import useGetDiscounts from '../../hooks/discounts/useGetDiscounts';
import {
  initiatePayment,
  invalidateCreateSplitPaymentsGroup,
  invalidateGroupedPayments,
} from '../../services/payments';
import {
  getDefaultProviderCommissionType,
  getEmployeeServiceCommissionRate,
  getInitiatePaymentPayload,
  isGuestCheckout as checkIsGuestCheckout,
  isDiscountAllowed,
  isTipDisplayed,
} from '../../helpers/payments';
import {
  InitiatePaymentResponse,
  SplitPaymentOnFinishArgs,
} from '../../types/payments';
import useCheckoutFormState from '../../hooks/checkouts/useCheckoutFormState';
import { CheckoutFormProvider } from '../../context/CheckoutFormContext';
import useCheckoutPagination from '../../hooks/checkouts/useCheckoutPagination';
import useSelectedSplitPaymentsGroup from '../../hooks/checkouts/useSelectedSplitPaymentsGroup';
import { searchClient } from '../../algoliaconfig';
import { invalidateQueue } from '../../services/queue';
import {
  invalidateCustomerGiftCardBalances,
  invalidateGiftCardsPage,
} from '../../services/gift-cards';
import useGetCheckoutData from '../../hooks/checkouts/useGetCheckoutData';
import ValuesListener from './ValuesListener';
import RevalidateOnDeps from './RevalidateOnDeps';
import useGetCustomerGiftCardBalance from '../../hooks/giftCards/useGetCustomerGiftCardBalance';
import { validateGiftCardAmount } from '../../helpers/gift-cards';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import { getWaiverRequestStatus } from '../../helpers/waivers';
import CashTenderModal from './CashTenderModal';
import { CheckoutFormGuard } from './CheckoutFormGuard';
import CheckoutBody from '../checkout/CheckoutBody';

const stripePromise = loadStripe(STRIPE_PK as string);

export { CheckoutFormGuard } from './CheckoutFormGuard';

function CheckoutFormComponent() {
  const stripe = useStripe();
  const elements = useElements();

  const {
    setTerminalCheckoutModalToLoading: setTerminalCheckoutLoading,
    openCheckout,
    closeCheckout,
    openGuestReceipt,
    pendingGuestReceipt,
    setPendingGuestReceipt,
  } = useCheckoutForm();
  const setModalOpen = (open: boolean) =>
    open ? openCheckout() : closeCheckout();
  const confirm = useConfirm();
  const {
    state: {
      isFetching: isCheckoutFetching,
      isError: isErrorCheckout,
      isDebouncing: isDebouncingValues,
    },
  } = useCheckoutFormState();
  const isFetchingCheckout = isCheckoutFetching || isDebouncingValues;

  const { state } = React.useContext(AppContext);
  const { checkout } = state;
  const { user: loadedUser } = useUser<LoadedUser>();
  const {
    selectedAppointment: selectedAppointmentFromContext,
    setSelectedAppointmentUpdated,
  } = useCalendarSelectedAppointment();
  const { data: discounts = [] } = useGetDiscounts({
    activeOnly: true,
  });
  const { updateLoadingModal, closeLoadingModal } = useLoadingModal();

  const { setSelectedSplitPaymentsGroupId } = useSelectedSplitPaymentsGroup();
  const { resetCheckoutFailureData, setMostRecentPaymentId } =
    useCheckoutForm();

  const [formError, setFormError] = React.useState<any>('');
  const [isSplitPayment, setIsSplitPayment] = React.useState(false);
  const [totalCashAmount, setTotalCashAmount] = React.useState(0);
  // static selectedAppointment
  const [selectedAppointment] = React.useState(selectedAppointmentFromContext);
  const [appointment, setAppointment] =
    React.useState<SelectedAppointment | null>(selectedAppointment);

  const canCheckout = useFeatureGuard(Feature.CHECKOUT);
  const { active: redesignActive } = useBusinessWebRedesign();

  const { data: waivers } = useGetWaivers({
    businessId: appointment?.businessId,
  });
  const waiverStatus = getWaiverRequestStatus({
    appointment,
    waiverRequest: appointment?.waiverRequest,
    waivers,
  });

  const { set: setPaymentsPage } = useCheckoutPagination();
  const resetCheckoutPage = async () => {
    try {
      setPaymentsPage(0);
      setTimeout(() => invalidateGroupedPayments(), 0);
    } catch {
      // do nothing
    }
  };

  const loadedInitValues = React.useMemo<CheckoutFormValues>(() => {
    const employeeId =
      selectedAppointment?.employeeId ||
      (loadedUser?.business?.type === 'INDEPENDENT' ||
      !isUserAdminOrManager(loadedUser?.role)
        ? loadedUser?.employee?.employeeId
        : '');

    if (!selectedAppointment)
      return {
        ...initFormValues,
        employeeId,
      };

    const etsRate = getEmployeeServiceCommissionRate(
      loadedUser?.business?.employees ?? [],
      loadedUser?.business?.services ?? [],
      employeeId,
      selectedAppointment.serviceId,
    );
    const providerCommissionType = getDefaultProviderCommissionType(etsRate);

    const clientSource =
      selectedAppointment.customerGuest ?? selectedAppointment.customer;
    return {
      customer: {
        customerId: selectedAppointment.customerId,
        customerGuestId: selectedAppointment.customerGuestId ?? undefined,
        firstName: clientSource.firstName,
        lastName: clientSource.lastName,
        email: clientSource.email,
        phoneNumber: selectedAppointment.customer.phoneNumber,
        dob: clientSource.dob
          ? new Date(clientSource.dob).toISOString().split('T')[0]
          : undefined,
      },
      appointmentId: selectedAppointment.appointmentId,
      startDateTime:
        selectedAppointment.startDateTime || new Date().toISOString(),
      price: selectedAppointment.price || 0,
      employeeId,
      appointmentLengthInMinutes:
        selectedAppointment.appointmentLengthInMinutes || 0,
      tipAmt: initFormValues.tipAmt,
      paymentMethod: initFormValues.paymentMethod,
      providerCommissionType,
      isCashTaxed: true,
      giftCardType: 'internal',
    } as CheckoutFormValues;
  }, [
    selectedAppointment,
    loadedUser?.business?.type,
    loadedUser?.business?.employees,
    loadedUser?.business?.services,
    loadedUser?.role,
    loadedUser?.employee?.employeeId,
  ]);

  const [formValues, setFormValues] =
    React.useState<CheckoutFormValues>(loadedInitValues);

  const discountIsAllowed =
    !!discounts.length &&
    (!formValues.paymentMethod || isDiscountAllowed(formValues.paymentMethod));
  const selectedDiscount =
    discountIsAllowed && formValues.discountId
      ? discounts.find(discount => discount.id === formValues.discountId)
      : undefined;

  const checkoutData = useGetCheckoutData({
    appointment,
    values: formValues,
    discount: selectedDiscount ?? null,
  });

  const {
    totalPriceCents,
    isTotalCoveredByGiftCard,
    totalPriceWithoutGiftCardCents,
  } = checkoutData?.data ?? {};
  const totalDue = (totalPriceCents ?? 0) / 100;

  const isGuestCheckout = checkIsGuestCheckout({
    customer: formValues.customer,
    appointment,
  });

  const validationSchema = React.useMemo(
    () => getCheckoutSchema({ isGuestCheckout }),
    [isGuestCheckout],
  );

  const enabledPaymentMethods = React.useMemo<CheckoutPaymentMethod[]>(() => {
    const hasRetailItemsWithPrice = (formValues.customOrderItems ?? []).some(
      i => (i.price ?? 0) > 0,
    );
    const isFreeAllowed =
      totalDue <= 0 && (!hasRetailItemsWithPrice || !!selectedDiscount);
    const free: CheckoutPaymentMethod[] = isFreeAllowed ? ['FREE'] : [];
    const credit: CheckoutPaymentMethod[] = isGuestCheckout ? [] : ['CREDIT'];
    if (appointment) {
      return [
        ...credit,
        'MANUAL',
        'CASH',
        'DIGITAL',
        ...free,
        'GIFT_CARD',
        ...(appointment.businessId
          ? (['TERMINAL'] as CheckoutPaymentMethod[])
          : []),
      ];
    }
    return [
      'TERMINAL',
      ...credit,
      'MANUAL',
      'CASH',
      'DIGITAL',
      ...free,
      'GIFT_CARD',
    ];
  }, [
    appointment,
    totalDue,
    formValues.customOrderItems,
    selectedDiscount,
    isGuestCheckout,
  ]);

  const { data: giftCardBalance } = useGetCustomerGiftCardBalance({
    customer: formValues.customer,
  });

  const isZeroAllowed = React.useCallback(
    (values: CheckoutFormValues) =>
      isTotalCoveredByGiftCard || values.paymentMethod === 'FREE',
    [isTotalCoveredByGiftCard],
  );

  const isValidGiftCard = React.useMemo(
    () =>
      formValues.paymentMethod !== 'GIFT_CARD' ||
      // Guests always pay via external gift card — no balance to check.
      isGuestCheckout ||
      validateGiftCardAmount({
        amountCents: totalPriceWithoutGiftCardCents ?? 0,
        giftCardBalanceCents: giftCardBalance?.availableBalance ?? 0,
        giftCardType: formValues.giftCardType,
        isSplitPayment: false,
        totalPriceCents: totalPriceWithoutGiftCardCents ?? 0,
      }).isValid,
    [
      formValues.giftCardType,
      formValues.paymentMethod,
      giftCardBalance?.availableBalance,
      totalPriceWithoutGiftCardCents,
      isGuestCheckout,
    ],
  );

  const isSubmitDisabled = React.useCallback(
    (values: CheckoutFormValues, isSubmitting: boolean) => {
      if (
        !values.paymentMethod ||
        !canCheckout ||
        isErrorCheckout ||
        totalCashAmount > 0
      )
        return true;
      if (
        !isGuestCheckout &&
        (!values.customer?.phoneNumber || !values.customer?.firstName)
      )
        return true;
      const allowZero = isZeroAllowed(values);
      const isValidTotal = totalDue > 0 || allowZero;
      const isValidPrice =
        !values.appointmentId ||
        (allowZero || values.customOrderItems?.length
          ? values.price >= 0
          : values.price > 0);
      return isSubmitting || !isValidTotal || !isValidPrice || !isValidGiftCard;
    },
    [
      canCheckout,
      isErrorCheckout,
      totalCashAmount,
      isZeroAllowed,
      totalDue,
      isValidGiftCard,
      isGuestCheckout,
    ],
  );

  const getSubmitButtonText = React.useCallback(
    (values: CheckoutFormValues) => {
      if ((totalDue ?? 0) <= 0 && isTotalCoveredByGiftCard)
        return 'Record gift card payment';
      if ((totalDue ?? 0) <= 0 && isZeroAllowed(values))
        return appointment ? 'Complete appointment' : 'Complete checkout';
      const dollarAmount = formatDollarAmount(Math.max(0, totalDue ?? 0));
      if (values.paymentMethod === 'CASH' || values.paymentMethod === 'DIGITAL')
        return `Record ${dollarAmount} Payment`;
      if (values.paymentMethod === 'MANUAL') return `Checkout ${dollarAmount}`;
      if (values.paymentMethod === 'GIFT_CARD')
        return 'Record gift card payment';
      return `Request ${dollarAmount}`;
    },
    [appointment, isTotalCoveredByGiftCard, isZeroAllowed, totalDue],
  );

  const handleManualPayment = async (
    payload: CheckoutFormValues,
    checkoutResponse: InitiatePaymentResponse,
  ) => {
    const { paymentIntent } = checkoutResponse;
    if (!paymentIntent?.client_secret) {
      return {
        error: 'Failed to initiate payment. Please try again.',
      };
    }

    const submission = await elements?.submit();
    if (submission?.error) {
      return {
        error: submission.error.message,
      };
    }
    // get setup intent from card element rendered in child
    const cardElement = elements?.getElement('card');
    if (!cardElement || !stripe) {
      return {
        error: 'Invalid payment method. Please try again.',
      };
    }

    const result = await stripe.confirmCardPayment(
      paymentIntent?.client_secret,
      {
        payment_method: {
          card: cardElement,
        },
        save_payment_method: false,
        receipt_email: payload.customer?.email || undefined,
      },
    );
    if (result.error) {
      return {
        error: result.error.message,
      };
    }

    // Close checkout
    if (result.paymentIntent?.status === 'succeeded') {
      if (appointment) {
        const totalAmountDue = +(totalDue || 0).toFixed(2);
        const tipAmt = payload.tipAmt || 0;
        const tattooPrice = Math.max(0, totalAmountDue - tipAmt);
        await closeCheckoutSuccess(appointment.appointmentId, {
          tattooPrice: Number(tattooPrice.toFixed(2)),
          tipAmt: Number(tipAmt.toFixed(2)),
          paymentId: checkoutResponse.paymentIntent?.paymentId || undefined,
        });
      } else if (checkoutResponse.order?.orderId) {
        await closeOrderCheckout(checkoutResponse.order.orderId);
        // webhook may take a few seconds to update the order payments/transfers
        await new Promise(resolve => {
          setTimeout(resolve, 2000);
        });
      }
    }

    return {
      result,
    };
  };

  const onSplitPayment = async (values: CheckoutFormValues) => {
    if (
      Number(values.tipAmt || 0) > 0 &&
      isTipDisplayed({
        values,
        appointment,
        user: loadedUser,
      })
    ) {
      const accepted = await confirm({
        title: 'Tip will be removed',
        message:
          'The tip you entered will be removed from the total due. Tips can only be accepted through Terminal devices for split payments.',
      });
      if (!accepted) return;
    }

    await invalidateCreateSplitPaymentsGroup();
    setIsSplitPayment(true);
  };

  const onRefreshAppointment = async () => {
    if (selectedAppointment) {
      setSelectedAppointmentUpdated(true);
    }

    if (appointment?.appointmentId) {
      await invalidateAppointment(appointment?.appointmentId);
    }
  };

  const onComplete = async ({
    customerId,
    splitPaymentsGroupId,
    paymentIds,
    hasCashLeg,
    totalCashAmount: totalCashAmountFromArgs,
  }: SplitPaymentOnFinishArgs & {
    totalCashAmount?: number;
  }) => {
    resetCheckoutFailureData();

    if (isGuestCheckout && splitPaymentsGroupId && paymentIds?.length) {
      if (hasCashLeg) {
        // Defer to onCashTenderClosed so the receipt doesn't stack with the
        // overview's inline CashTenderModal.
        setPendingGuestReceipt({
          paymentId: paymentIds[0],
          splitPaymentsGroupId,
        });
      } else {
        // No cash leg — open immediately, above the overview via JSX order.
        openGuestReceipt(paymentIds[0]);
      }
    }

    if (customerId) {
      addCustomerAlgolia(customerId, loadedUser?.business.id || '');
    }

    if (splitPaymentsGroupId) {
      setSelectedSplitPaymentsGroupId(splitPaymentsGroupId);
    }

    onRefreshAppointment();

    if (appointment?.status === 'QUEUED') {
      invalidateQueue();
    }

    setIsSplitPayment(false);

    await searchClient.clearCache();

    await resetCheckoutPage();
    invalidateGiftCardsPage();
    invalidateCustomerGiftCardBalances();

    // Opened for split payments in SplitPaymentsGroupOverview
    if (
      totalCashAmountFromArgs &&
      totalCashAmountFromArgs > 0 &&
      !splitPaymentsGroupId
    ) {
      setTotalCashAmount(totalCashAmountFromArgs);
    } else {
      setModalOpen(false);
    }
  };

  const handleCashTenderDismissSuccess = () => {
    setTotalCashAmount(0);
    // CASH single-payment stores pending with null splitPaymentsGroupId.
    const receiptPaymentId =
      pendingGuestReceipt && !pendingGuestReceipt.splitPaymentsGroupId
        ? pendingGuestReceipt.paymentId
        : null;
    setPendingGuestReceipt(null);
    setModalOpen(false);
    if (receiptPaymentId) {
      openGuestReceipt(receiptPaymentId);
    }
  };

  return (
    <Box
      style={{
        width: redesignActive ? '100%' : 900,
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={loadedInitValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, setValues }) => {
          if (
            !canCheckout ||
            isFetchingCheckout ||
            isErrorCheckout ||
            !isValidGiftCard ||
            totalCashAmount > 0
          )
            return;

          if (
            waiverStatus === 'notCompleted' ||
            waiverStatus === 'pendingEmployee'
          ) {
            const waiverMessage =
              waiverStatus === 'pendingEmployee'
                ? 'The employee portion of the waiver has not been completed. Are you sure you want to proceed with checkout?'
                : 'The waiver for this appointment has not been completed by the customer. Are you sure you want to proceed with checkout?';
            const accepted = await confirm({
              title: 'Waiver Incomplete',
              message: waiverMessage,
            });
            if (!accepted) return;
          }

          if (
            (totalDue ?? 0) <= 0 &&
            isZeroAllowed(values) &&
            !isTotalCoveredByGiftCard
          ) {
            const accepted = await confirm({
              title: 'Free Checkout',
              message: appointment
                ? 'Once you check this appointment out, you will not be able to go back and edit the price to collect a payment. Are you sure you want to continue?'
                : 'This order will be recorded as a $0 payment. Are you sure you want to continue?',
            });
            if (!accepted) return;
          }

          const hasGiftCardItem = values.customOrderItems?.some(
            i => i.category === 'GIFT_CARD',
          );
          if (isGuestCheckout && hasGiftCardItem) {
            confirm({
              title: 'Customer required',
              message:
                'Gift cards must be issued to a customer. Add a customer or remove the gift card to continue.',
              positiveText: 'Okay',
              negativeText: '',
            });
            setSubmitting(false);
            return;
          }
          const isMissingEmail = !values.customer?.email && hasGiftCardItem;
          if (isMissingEmail) {
            confirm({
              title: 'Missing Email',
              message:
                'An email address is required for gift card purchases. Please enter an email address for the customer to continue.',
              positiveText: 'Okay',
              negativeText: '',
            });
            setSubmitting(false);
            return;
          }

          const header =
            values.paymentMethod === 'MANUAL'
              ? 'Processing...'
              : 'Initiating Checkout...';
          updateLoadingModal({
            header,
            message:
              'This could take a minute. Please do not refresh the page.',
            status: 'loading',
          });

          setSubmitting(true);
          if (
            !isGuestCheckout &&
            (!values.customer?.phoneNumber || !values.customer?.firstName)
          ) {
            setFormError('Client information is required');
            setSubmitting(false);
            return;
          }

          let closeModal = true;
          try {
            const { data: checkoutPayload, overridePaymentMethod } =
              getInitiatePaymentPayload({
                appointmentId: appointment?.appointmentId,
                businessId: appointment?.businessId || loadedUser?.business?.id,
                values,
                discount: selectedDiscount,
                failureData: checkout.checkoutFailureData,
                guest: isGuestCheckout,
              });
            const checkoutResponse = await initiatePayment(
              checkoutPayload,
              values.customer,
              { guest: isGuestCheckout },
            );
            const paymentMethod = overridePaymentMethod || values.paymentMethod;

            if (paymentMethod === 'MANUAL') {
              const paymentResult = await handleManualPayment(
                values,
                checkoutResponse,
              );
              if (paymentResult.error) {
                closeModal = false;
                if (checkoutResponse?.order?.orderId) {
                  setValues(prev => ({
                    ...prev,
                    failedOrderId: checkoutResponse.order.orderId,
                    paymentIntentId: checkoutResponse.paymentIntent?.id,
                  }));
                }
                toast.error(paymentResult.error);
                closeLoadingModal();
                return;
              }
            }

            if (checkoutResponse) {
              if (paymentMethod === 'CREDIT') {
                toast.success('Checkout link sent to customer!');
              }
              if (paymentMethod === 'TERMINAL') {
                setTerminalCheckoutLoading();
                toast.success('Charge sent to terminal');
              }
              if (paymentMethod === 'CASH' || paymentMethod === 'DIGITAL') {
                toast.success('Payment recorded');
              }
              if (paymentMethod === 'GIFT_CARD') {
                toast.success('Gift card payment recorded');
              }
              if (checkoutResponse.paymentIntent?.paymentId) {
                setMostRecentPaymentId(
                  checkoutResponse.paymentIntent?.paymentId,
                );
              }
            }

            await onComplete({
              customerId: checkoutResponse?.customerId,
              totalCashAmount: paymentMethod === 'CASH' ? totalDue ?? 0 : 0,
            });

            const guestPaymentId = isGuestCheckout
              ? checkoutResponse?.paymentIds?.[0] ?? null
              : null;
            if (guestPaymentId) {
              // CASH waits for the tender modal to close.
              // TERMINAL waits for setTerminalCheckoutModalToSuccess to fire.
              if (paymentMethod === 'CASH') {
                setPendingGuestReceipt({
                  paymentId: guestPaymentId,
                  splitPaymentsGroupId: null,
                });
              } else if (paymentMethod !== 'TERMINAL') {
                openGuestReceipt(guestPaymentId);
              }
            }
          } catch (error: any) {
            if (error?.response?.data?.message) {
              setFormError(error?.response?.data?.message);
            } else {
              setFormError(
                'Error initiating checkout. Please make sure all fields are filled in and try again.',
              );
            }
            toast.error('Error initiating checkout');
          } finally {
            setSubmitting(false);
            if (closeModal) closeLoadingModal();
          }
        }}>
        {({ isSubmitting, values, isValid }) => (
          <>
            <ValuesListener debounceTime={0} onChange={setFormValues} />
            <RevalidateOnDeps deps={[isGuestCheckout]} />
            <CheckoutFormGuard
              appointment={appointment}
              enabledPaymentMethods={enabledPaymentMethods}
            />
            <CheckoutBody
              selectedAppointment={selectedAppointment}
              appointment={appointment}
              setAppointment={setAppointment}
              selectedDiscount={selectedDiscount}
              checkoutData={checkoutData}
              enabledPaymentMethods={enabledPaymentMethods}
              formError={formError}
              waiverStatus={waiverStatus}
              submitText={getSubmitButtonText(values)}
              submitDisabled={
                !isValid || isSubmitDisabled(values, isSubmitting)
              }
              isSplitPayment={isSplitPayment}
              onSplitPayment={() => onSplitPayment(values)}
              onCloseSplitPayment={() => setIsSplitPayment(false)}
              onCheckoutSuccess={onComplete}
              onRefreshAppointment={onRefreshAppointment}
            />
          </>
        )}
      </Formik>
      <CashTenderModal
        open={totalCashAmount > 0}
        onClose={handleCashTenderDismissSuccess}
        saleTotalDollars={totalCashAmount}
        onDismissSuccess={handleCashTenderDismissSuccess}
      />
    </Box>
  );
}

export default function CheckoutForm() {
  const stripeOptions = useStripeOptions();
  return (
    <CheckoutFormProvider>
      <Elements stripe={stripePromise} options={stripeOptions}>
        <CheckoutFormComponent />
      </Elements>
    </CheckoutFormProvider>
  );
}
